import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";

import "../css/Login.css";
import { CompanyContext } from "./CompanyContext";
import CompanyLogo from "./CompanyLogo";

import SmallLogin from "../assets/small-login.jpg"
import ecgLogo from "../assets/ecg.png";


const companies = [
  { id: "MelbTruss", logolight: "/logo3-white.png", logodark: "/logo3-black.png" },
  { id: "LockupFixing", logolight: "/fixing-3-white.png", logodark: "/fixing-3-black.png" },
  { id: "PromoDoors", logolight: "/promodoors-inv.png", logodark: "/promodoors-trnsprnt.png" },
];

const roles = {
  PromoDoors: ["admin", "producer", "driver"],
  default: ["admin", "driver"],
};

const Login = () => {
  const [step, setStep] = useState(0);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [userType, setUserType] = useState("");
  const { company, setCompany } = useContext(CompanyContext);
  const navigate = useNavigate();

  const next = () => setStep((prev) => prev + 1);
  const back = () => setStep((prev) => Math.max(prev - 1, 0));

  const handleCompany = (c) => {
    setCompany(c);
    next();
  };

  const handleUserType = (type) => {
    setUserType(type);
    next();
  };



  useEffect(() => {
    const token = Cookies.get("token");
    if (token && company) {
      axios
        .get(`${process.env[`REACT_APP_R_BASE_SERVER_URL_${company.toUpperCase()}`]}/verify-token`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          if (response.data.valid) {
            navigate(`/${company.toLowerCase()}/${response.data.userType}`);
          }
        })
        .catch(() => {
          Cookies.remove("token");
          Cookies.remove("company");
          setCompany("");
        });
    }
  }, [navigate, company]);

  const [theme, setTheme] = useState(() =>
    localStorage.getItem("theme") || (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const newTheme = document.documentElement.classList.contains("dark") ? "dark" : "light";
      setTheme(newTheme);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => (prev === "dark" ? "light" : "dark"));

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(
        `${process.env[`REACT_APP_R_BASE_SERVER_URL_${company.toUpperCase()}`]}/${userType}/login`,
        { username, password, company }
      );
      Cookies.set("token", response.data.token);
      navigate(`/${company.toLowerCase()}/${userType}`);
    } catch (error) {
      console.error(error);
      alert("Invalid username or password");
    }
  };

  const handleCompanySelection = (selectedCompany) => {
    setCompany(selectedCompany);
    setUserType("");
  };


  const handleBack = () => {
    if (userType) {
      setUserType("");
    } else if (company) {
      setCompany("");
    }
  };

  const getCompanyImage = (company) => {
    if (company) {
      let companyData = companies.find(c => c.id === company)
      return companyData;
    }
  }

  const companyData = companies.find(c => c.id === company);

  return (
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
              w-[90%] max-w-5xl h-[32rem] rounded-2xl shadow-2xl 
              flex overflow-hidden bg-gradient-to-r dark:from-neutral-900/60 dark:to-neutral-600/40 backdrop-blur-[1px] from-neutral-400/60 to-neutral-200/40">



      {/* LEFT PANEL */}
      <div className="hidden relative lg:block w-1/2 h-full overflow-hidden">
        <div className="absolute inset-0"></div>

        {/* Main logo container - centered */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center relative z-10">
            {/* Logo container with enhanced styling */}
            <div className="relative mb-6">
              {/* Glow effect behind logo */}
              <div className="absolute inset-0 dark:bg-white/20 bg-black/20 rounded-3xl blur-xl scale-110"></div>

              {/* Main logo container */}
              <div className="relative">
                <img
                  className="w-56 h-auto mx-auto drop-shadow-lg"
                  src={ecgLogo}
                  alt="Logo"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="w-full lg:w-1/2 h-full px-8 py-10 flex flex-col justify-start">
        <div className="absolute inset-0"></div>

        <button
          onClick={toggleTheme}
          className="absolute bottom-3 left-[50%] transform -translate-x-[50%] dark:bg-zinc-500/60 dark:text-zinc-300 bg-zinc-300/60 text-zinc-700 px-8 py-1 border border-transparent rounded-full "
        >
          {theme === "dark" ?
            <>
              <p className="admin-button-name font-semibold !m-0">🌙 Dark Mode</p>
            </>
            :
            <>
              <p className="admin-button-name font-semibold !m-0">☀️ Light Mode</p>
            </>
          }
        </button>
        <h1 className="text-3xl font-bold text-center mb-8 text-gray-800 dark:text-white">Login</h1>



        <div className="relative flex-1 w-full overflow-hidden">
          {/* Company Selection */}
          <div className={`absolute top-0 left-0 w-full transition-all duration-700 
                    ${step === 0 ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0'}`}>
            <h2 className="text-xl font-semibold mb-4 text-center dark:text-dark-text">Select Company</h2>
            <div className="grid gap-4">
              {companies.map((c) => (
                <button
                  key={c.id}
                  onClick={() => handleCompany(c.id)}
                  className="rounded-lg p-3 flex justify-center items-center dark:bg-white/20 bg-zinc-600/20"
                >
                  <img src={theme === "dark" ? process.env.PUBLIC_URL + c.logolight : process.env.PUBLIC_URL + c.logodark} alt={c.id} className="h-12" />


                </button>
              ))}
            </div>
          </div>

          {/* User Role Selection */}
          <div className={`absolute top-0 left-0 w-full transition-all duration-700 
                    ${step === 1 ? 'translate-x-0 opacity-100' : step < 1 ? 'translate-x-full opacity-0' : '-translate-x-full opacity-0'}`}>

            <div className={`${step === 0 ? "opacity-0" : "opacity-100"} transition-all ease-in-out`}>
              <img
                src={theme === "dark"
                  ? process.env.PUBLIC_URL + companyData?.logolight
                  : process.env.PUBLIC_URL + companyData?.logodark
                }
                alt={companyData?.id}
                className="h-12 mx-auto mb-6"
              />
            </div>

            <h2 className="text-xl font-semibold mb-4 text-center dark:text-dark-text">Select Role</h2>
            <div className="flex flex-col gap-4">
              {(roles[company] || roles.default).map((r) => (
                <button
                  key={r}
                  onClick={() => handleUserType(r)}
                  className="dark:bg-white/20 px-4 py-3 rounded-lg text-center capitalize dark?hover:bg-white/30 transition dark:text-dark-text bg-zinc-600/20"
                >
                  {r}
                </button>
              ))}
            </div>
            <button onClick={back} className="mt-4 text-base text-red-500">← Back</button>
          </div>

          {/* Login Form */}
          <div className={`absolute top-0 left-0 w-full transition-all duration-700 
                    ${step === 2 ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}>
            {/* <h2 className="text-xl font-semibold mb-4 text-center dark:text-dark-text">Welcome Back</h2> */}
            <div className={`${step === 0 ? "opacity-0" : "opacity-100"} transition-all ease-in-out`}>
              <img
                src={theme === "dark"
                  ? process.env.PUBLIC_URL + companyData?.logolight
                  : process.env.PUBLIC_URL + companyData?.logodark
                }
                alt={companyData?.id}
                className="h-12 mx-auto mb-6"
              />
            </div>
            <form onSubmit={handleLogin}>
              <div className="mb-4">
                <label className="block dark:text-dark-text/50 text-zinc-700 capitalize">{userType} Username</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block dark:text-dark-text/50 text-zinc-700">Password</label>
                <input
                  type="password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  required
                />
              </div>
              <button type="submit" className="bg-notify-green hover:bg-green-500 transition ease-in-out text-white py-2 px-4 w-full rounded-md">
                Login
              </button>
            </form>
            <button onClick={back} className="mt-4 text-base text-red-500">← Back</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
